import { cn } from "@d12labs/utils";
import { Check, ChevronDown, ListFilter, Search, X } from "lucide-react";
import { type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { fmtUsd } from "../aggregate";
import type { RepoSpend } from "../overview";

/** Segmented control: one hairline track, the selected option lifts to surface-3. */
export function Segmented<T extends string>({
	label,
	options,
	value,
	onChange,
}: {
	label: string;
	options: { value: T; label: string }[];
	value: T;
	onChange: (value: T) => void;
}) {
	return (
		<fieldset className="m-0 flex items-center rounded-md border border-hairline bg-surface-1 p-0.5">
			<legend className="sr-only">{label}</legend>
			{options.map((option) => (
				<button
					key={option.value}
					type="button"
					aria-pressed={value === option.value}
					onClick={() => onChange(option.value)}
					className={cn(
						"h-7 whitespace-nowrap rounded px-2.5 text-body-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/50",
						value === option.value
							? "bg-surface-3 text-foreground"
							: "text-ink-subtle hover:text-foreground",
					)}
				>
					{option.label}
				</button>
			))}
		</fieldset>
	);
}

export function ToolbarDivider() {
	return <span aria-hidden className="mx-1 h-5 w-px flex-none bg-hairline" />;
}

/**
 * "All repos" dropdown — filter input + checkbox list sorted by spend. Replaces
 * the wall of repo pills. Open state is owned by the page so the Top repos
 * panel can open it too.
 */
export function RepoFilter({
	options,
	selected,
	onChange,
	open,
	onOpenChange,
}: {
	options: RepoSpend[];
	selected: string[];
	onChange: (repos: string[]) => void;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const rootRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const [filter, setFilter] = useState("");

	useEffect(() => {
		if (!open) {
			setFilter("");
			return;
		}
		inputRef.current?.focus();
		const onPointer = (event: PointerEvent) => {
			if (!rootRef.current?.contains(event.target as Node)) onOpenChange(false);
		};
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") onOpenChange(false);
		};
		document.addEventListener("pointerdown", onPointer);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("pointerdown", onPointer);
			document.removeEventListener("keydown", onKey);
		};
	}, [open, onOpenChange]);

	// Selected repos stay listed even when other filters leave them no spend.
	const rows = useMemo(() => {
		const known = new Set(options.map((o) => o.repo));
		const all = [
			...options,
			...selected.filter((r) => !known.has(r)).map((repo) => ({ repo, spend: 0, sessions: 0 })),
		];
		const needle = filter.trim().toLowerCase();
		return needle ? all.filter((o) => o.repo.toLowerCase().includes(needle)) : all;
	}, [options, selected, filter]);

	const chosen = new Set(selected);
	const toggle = (repo: string) => {
		const next = new Set(chosen);
		if (next.has(repo)) next.delete(repo);
		else next.add(repo);
		onChange([...next]);
	};

	const buttonLabel =
		selected.length === 0 ? "All repos" : selected.length === 1 ? selected[0] : "Repos";

	return (
		<div ref={rootRef} className="relative">
			<button
				type="button"
				aria-haspopup="dialog"
				aria-expanded={open}
				onClick={() => onOpenChange(!open)}
				className={cn(
					"inline-flex h-8 max-w-[240px] items-center gap-2 rounded-md border px-2.5 text-body-sm font-medium text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/50",
					open || selected.length
						? "border-hairline-strong bg-surface-2"
						: "border-hairline bg-surface-1 hover:bg-surface-2",
				)}
			>
				<ListFilter className="size-3.5 flex-none text-ink-subtle" />
				<span className="truncate">{buttonLabel}</span>
				<span className="text-meta tabular-nums text-ink-subtle">
					{selected.length || options.length}
				</span>
				<ChevronDown className="size-3.5 flex-none text-ink-subtle" />
			</button>

			{open ? (
				<div
					role="dialog"
					aria-label="Filter by repo"
					className="absolute left-0 top-[calc(100%+6px)] z-30 w-[300px] overflow-hidden rounded-lg border border-hairline-strong bg-popover shadow-2xl shadow-black/40"
				>
					<div className="flex h-9 items-center gap-2 border-b border-hairline px-3">
						<Search className="size-3.5 flex-none text-ink-subtle" />
						<input
							ref={inputRef}
							value={filter}
							onChange={(event) => setFilter(event.target.value)}
							placeholder="Filter repos…"
							aria-label="Filter repos"
							className="min-w-0 flex-1 bg-transparent text-body-sm text-foreground outline-none placeholder:text-ink-tertiary"
						/>
					</div>
					<div className="max-h-[280px] overflow-y-auto py-1">
						{rows.length === 0 ? (
							<p className="px-3 py-2 text-body-sm text-ink-subtle">No repos match.</p>
						) : (
							rows.map((row) => {
								const on = chosen.has(row.repo);
								return (
									<label
										key={row.repo}
										className="flex h-8 cursor-pointer items-center gap-2.5 px-3 text-body-sm text-ink-muted hover:bg-surface-3 has-[:focus-visible]:bg-surface-3"
									>
										<input
											type="checkbox"
											checked={on}
											onChange={() => toggle(row.repo)}
											className="sr-only"
										/>
										<span
											aria-hidden
											className={cn(
												"flex size-3.5 flex-none items-center justify-center rounded-sm border",
												on
													? "border-primary bg-primary text-primary-foreground"
													: "border-hairline-strong",
											)}
										>
											{on ? <Check className="size-2.5" strokeWidth={3} /> : null}
										</span>
										<span className="flex-1 truncate">{row.repo}</span>
										<span className="font-mono text-[11px] tabular-nums text-ink-subtle">
											{fmtUsd(row.spend)}
										</span>
									</label>
								);
							})
						)}
					</div>
					<div className="flex items-center justify-between border-t border-hairline px-3 py-2 text-meta">
						<span className="text-ink-subtle">Sorted by spend</span>
						<button
							type="button"
							onClick={() => onChange([])}
							disabled={selected.length === 0}
							className="text-ink-muted hover:text-foreground disabled:opacity-40"
						>
							Clear
						</button>
					</div>
				</div>
			) : null}
		</div>
	);
}

function isTyping(target: EventTarget | null): boolean {
	const el = target as HTMLElement | null;
	return !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
}

/** Search box with a global `/` shortcut. Debounces into the URL. */
export function SessionSearch({
	value,
	onChange,
	inputRef,
	placeholder = "Search sessions…",
	label = "Search sessions",
}: {
	value: string;
	onChange: (value: string) => void;
	inputRef: RefObject<HTMLInputElement | null>;
	placeholder?: string;
	label?: string;
}) {
	const [draft, setDraft] = useState(value);
	useEffect(() => setDraft(value), [value]);
	useEffect(() => {
		if (draft === value) return;
		const timer = setTimeout(() => onChange(draft), 200);
		return () => clearTimeout(timer);
	}, [draft, value, onChange]);

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key !== "/" || event.metaKey || event.ctrlKey || isTyping(event.target)) return;
			event.preventDefault();
			inputRef.current?.focus();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [inputRef]);

	return (
		<div className="flex h-8 w-[220px] items-center gap-2 rounded-md border border-hairline bg-surface-1 px-2.5 focus-within:border-hairline-strong">
			<Search className="size-3.5 flex-none text-ink-subtle" />
			<input
				ref={inputRef}
				value={draft}
				onChange={(event) => setDraft(event.target.value)}
				onKeyDown={(event) => {
					if (event.key === "Escape") {
						setDraft("");
						onChange("");
						event.currentTarget.blur();
					}
				}}
				placeholder={placeholder}
				aria-label={label}
				className="min-w-0 flex-1 bg-transparent text-body-sm text-foreground outline-none placeholder:text-ink-tertiary"
			/>
			{draft ? (
				<button
					type="button"
					aria-label="Clear search"
					onClick={() => {
						setDraft("");
						onChange("");
					}}
					className="text-ink-subtle hover:text-foreground"
				>
					<X className="size-3.5" />
				</button>
			) : (
				<kbd className="rounded border border-hairline px-1 font-mono text-[10px] leading-4 text-ink-subtle">
					/
				</kbd>
			)}
		</div>
	);
}
